import {
    CheckIcon,
    DocumentTextIcon,
    LinkIcon,
    PlusCircleIcon,
    TrashIcon,
    ZoomInIcon,
} from '@heroicons/react/outline'
import { formateDateWithProofFormat } from '../helpers/proofs'
import { ModalStore } from '../state/ModalStore'
import ImageModal from './Modals/ImageModal'
import PdfModal from './Modals/PdfModal'
import CsvModal from './Modals/CsvModal'
import DocxModal from './Modals/DocxModal'
import { useEffect, useRef, useState } from 'react'
import {
    CUSTOMER_CUSTOM_IMAGES_KEY,
    customerCustomImages,
    getErrorMessage,
    isCustomerUnAuthorized,
    isImage,
    log,
} from '../helpers/utils'
import ConfirmDialog from './Modals/ConfirmDialog'
import {
    CustomerAPI,
    CustomerFetcher,
    SaveCustomerAPI,
} from '../apis/CustomerAPI'
import useSWR, { mutate } from 'swr'
import Loader from './Loader'
import SuccessAlert from './Alerts/SuccessAlert'
import ErrorAlert from './Alerts/ErrorAlert'
import { UserStore } from '../state/UserStore'
import Link from 'next/link'
import { Secondary } from './Buttons/Secondary'
import { UserUploadStore } from '../state/UserUploadStore'
import HelperText from './Inputs/HelperText'

const BottomActionButton = ({ image, handlePreview }) => {
    return isImage(image.url) ? (
        <button
            type="button"
            className="absolute right-0 m-2 bottom-0 bg-white p-1  opacity-80 hover:opacity-100"
            onClick={(e) => handlePreview(image, e)}>
            <ZoomInIcon className="w-6 h-6" />
        </button>
    ) : (
        <button
            type="button"
            className="absolute right-0 m-2 bottom-0 bg-white p-1  opacity-80 hover:opacity-100"
            onClick={(e) => handlePreview(image, e)}>
            <LinkIcon className="w-6 h-6" />
        </button>
    )
}

const SelectionComponent = ({ isSelected, image }) => {
    return isSelected.includes(image.url) ? (
        <div className={'absolute inset-0 flex items-center justify-center'}>
            <div className="bg-white opacity-80 w-full h-full absolute"></div>
            <CheckIcon className="w-16 h-16 text-olm-green z-10" />
        </div>
    ) : null
}

function UserUploadsComponent({
    canDelete = false,
    canUpload = true,
    canSelect = false,
    onSelect,
    selected,
    isUploadsPage = false,
    noOfColumns = 4,
}) {
    const { data: customer, error } = useSWR(
        CustomerAPI.getCustomer,
        CustomerFetcher,
    )

    const [success, setSuccess] = useState('')
    const [loading, setLoading] = useState(false)
    const [formError, setFormError] = useState(null)
    const addModal = ModalStore((state) => state.add)
    const [uploads, setUploads] = useState([])
    const updateCustomer = UserStore((state) => state.updateCustomer)

    const addUploads = UserUploadStore((state) => state.add)
    const userUploads = UserUploadStore((state) => state.uploads)

    const [isSelected, setIsSelected] = useState([])
    const fileInputRef = useRef()
    const acceptedFormats = '.png, .jpg, .gif, .doc, .docx, .pdf'

    const uploadFizeSize = 20 // MB

    const sizeClasses = noOfColumns
        ? // ? ` grid-cols-${noOfColumns} gap-${noOfColumns} `
          'grid-cols-2 gap-3 md:grid-cols-5 md:gap-5'
        : 'grid-cols-2 gap-3 md:grid-cols-5 md:gap-5'

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            // Reset the value of the file input
            fileInputRef.current.value = null
            fileInputRef.current.click() // Trigger the file input click event
        }
    }

    const handleFileChange = (event) => {
        setFormError(null)
        const files = event.target.files
        const maxFileSize = 1048576 * uploadFizeSize // 1 MB in bytes

        const validFiles = Array.from(files).filter(
            (file) => file.size <= maxFileSize,
        )

        if (validFiles.length > 0) {
            // Submit the form with valid files
            handleSubmit(validFiles)
        } else {
            // No valid files selected or all files exceeded the size limit
            // Handle the error here, for example, display an error message to the user
            setFormError(
                `No valid files selected or all files exceeded the max size limit (${uploadFizeSize} MB).`,
            )
        }
    }

    const afterUpload = async (images) => {
        const isGuestUser = isCustomerUnAuthorized(customer)

        if (!isGuestUser) {
            await mutate(CustomerAPI.getCustomer)
        }
        setSuccess('Files Uploaded Successfully.')
        if (isGuestUser) {
            addUploads(images)
            const uploads = [...userUploads, ...images]
            setUploads(uploads)
        }
        setFormError(false)
        setLoading(false)
        handleMultiSelect(images)
    }

    const handleMultiSelect = (images) => {
        images.forEach((image) => {
            handleSelect(image)
        })
    }

    const handleSubmit = async (files) => {
        try {
            setLoading(true)
            setFormError(false)
            let data = {
                [CUSTOMER_CUSTOM_IMAGES_KEY]: [...files],
            }
            const images = await SaveCustomerAPI.uploadImages({
                customer,
                data,
            })
            afterUpload(images)
        } catch (e) {
            log(e)
            setFormError(getErrorMessage(e))
            setLoading(false)
        } finally {
            fileInputRef.current.value = null
        }
    }

    const isPdf = (url) => /\.pdf(\?|$)/i.test(String(url).toLowerCase())
    const isCsv = (url) => /\.csv(\?|$)/i.test(String(url).toLowerCase())
    const isDocx = (url) => /\.docx(\?|$)/i.test(String(url).toLowerCase())

    const handlePreview = (image, e) => {
        e && e.stopPropagation && e.stopPropagation()
        const { name, url } = image
        if (isImage(url)) return addModal(<ImageModal name={name} url={url} />)
        if (isPdf(url)) return addModal(<PdfModal name={name} url={url} />)
        if (isCsv(url)) return addModal(<CsvModal name={name} url={url} />)
        if (isDocx(url)) return addModal(<DocxModal name={name} url={url} />)
        window.open(url, '_blank', 'noopener,noreferrer')
    }

    const deleteFile = async (url) => {
        try {
            setLoading(true)
            await SaveCustomerAPI.deleteImage(customer, url)
            setSuccess('Files Deleted Successfully.')
            await mutate(CustomerAPI.getCustomer)
        } catch (e) {
            log(getErrorMessage(e))
            setFormError(getErrorMessage(e))
        } finally {
            setLoading(false)
            setFormError(false)
        }
    }

    const handleDelete = (url) => {
        if (!canDelete) return
        addModal(
            <ConfirmDialog
                onConfirm={() => deleteFile(url)}
                title={'Delete file'}
                description={'Are you sure you want to delete this file?'}
            />,
        )
    }

    const handleSelect = ({ url }) => {
        if (!canSelect) return
        setIsSelected((prevState) => {
            const state = [...prevState]
            const updatedState = state.includes(url)
                ? state.filter((s) => s !== url)
                : [...state, url]
            onSelect && onSelect(updatedState)
            return updatedState
        })
    }

    useEffect(() => {
        if (!isCustomerUnAuthorized(customer)) {
            updateCustomer(customer)
            // log(customerCustomImages(customer))
            setUploads(customerCustomImages(customer))
        }
    }, [customer])

    useEffect(() => {
        if (isCustomerUnAuthorized(customer)) {
            setUploads(userUploads)
        }
        return () => setUploads([])
    }, [])

    useEffect(() => {
        if (selected) {
            setIsSelected(selected)
        }
    }, [selected, uploads])

    // if (!customer && error)
    //     return (
    //         <div className="text-center m-auto justify-center flex max-w-lg ">
    //             <div className="text-center space-y-3">
    //                 <p className="flex space-x-1 text-olm-black font-medium text-sm">
    //                     <span>
    //                         To access your saved uploads,
    //                         <Link href="/login">
    //                             <a className="underline hover:no-underline ml-1 ">
    //                                 log in
    //                             </a>
    //                         </Link>
    //                         <span className="ml-1">or</span>
    //                         <Link href="/create-account">
    //                             <a className="underline hover:no-underline ml-1 ">
    //                                 create an account.
    //                             </a>
    //                         </Link>
    //                     </span>
    //                 </p>
    //             </div>
    //         </div>
    //     )

    return (
        <div className="w-full">
            {loading && <Loader />}

            <div className="space-y-2 mb-2 text-left">
                {success && <SuccessAlert success={success} />}
                {formError && <ErrorAlert error={formError} />}

                {canUpload && (
                    <div
                        className={`flex
                    ${isUploadsPage ? 'justify-end' : 'justify-center'}
                    `}>
                        <form onSubmit={(event) => event.preventDefault()}>
                            <input
                                data-input-id="user-file-upload"
                                type="file"
                                ref={fileInputRef}
                                multiple
                                className="hidden"
                                onChange={handleFileChange}
                                accept={acceptedFormats}
                            />
                            <Secondary
                                type="button"
                                onClick={handleButtonClick}>
                                Upload Files
                            </Secondary>
                            <div className="justify-center flex">
                                <HelperText>
                                    Max Upload Size: {uploadFizeSize} MB
                                </HelperText>
                            </div>
                            {!isUploadsPage && (
                                <p className="text-sm text-olm-black font-medium w-52 mt-2">
                                    Accepted formats: {acceptedFormats}
                                </p>
                            )}
                        </form>
                    </div>
                )}
            </div>

            {!isUploadsPage ? (
                <div className="text-xl border-t-2 pt-2 my-4 border-olm font-medium mb-4 text-left tracking-tight text-olm-black">
                    My Uploads
                </div>
            ) : (
                <div className="my-4"></div>
            )}

            {uploads && uploads.length > 0 ? (
                <div
                    data-button-id="user-uploads-wrapper"
                    className={`grid ${sizeClasses}`}>
                    {uploads.map((image) => (
                        <button
                            data-button-class="user-upload"
                            key={image.url}
                            className="relative h-64 md:h-48 overflow-hidden bg-slate-50 border border-slate-200 rounded-md
                                  items-center flex flex-col">
                            <div className="flex-grow flex flex-col w-full">
                                {isImage(image.url) ? (
                                    <div
                                        className="relative cursor-pointer flex-grow"
                                        onClick={() =>
                                            handleSelect({ url: image.url })
                                        }>
                                        <div className="w-full h-32 overflow-hidden">
                                            <img
                                                src={image.url}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        {canSelect && (
                                            <SelectionComponent
                                                {...{
                                                    isSelected,
                                                    image,
                                                }}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        onClick={() =>
                                            handleSelect({ url: image.url })
                                        }
                                        className="flex p-1 cursor-pointer items-center justify-center flex-grow w-full relative">
                                        <p
                                            target="_blank"
                                            className="text-olh py-1 rounded-md text-sm p-2
                                                       whitespace-nowrap overflow-hidden overflow-ellipsis text-center flex justify-center items-center"
                                            rel="noreferrer">
                                            <DocumentTextIcon className="w-28 h-28" />
                                        </p>

                                        {canSelect && (
                                            <SelectionComponent
                                                {...{
                                                    isSelected,
                                                    image,
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col justify-center h-full">
                                <div className="text-olm-black text-sm font-medium w-full">
                                    <p className="text-center">{image?.name}</p>

                                    <p className="text-center">
                                        {formateDateWithProofFormat(
                                            image.upload_time,
                                        )}{' '}
                                    </p>
                                </div>
                            </div>

                            {canDelete && (
                                <button
                                    className="absolute p-1 m-2 top-0 right-0 bg-white opacity-80 hover:opacity-100"
                                    onClick={() => handleDelete(image.url)}>
                                    <TrashIcon className="w-6 h-6 text-red-500 cursor-pointer"></TrashIcon>
                                </button>
                            )}

                            <BottomActionButton
                                image={image}
                                handlePreview={handlePreview}
                            />
                        </button>
                    ))}
                </div>
            ) : (
                <div className="text-center m-auto justify-center flex max-w-lg ">
                    <div className="text-center space-y-5">
                        <button
                            onClick={handleButtonClick}
                            className="rounded-md border-4 w-40 border-dotted max-w-xs m-auto py-4 h-40 items-center flex">
                            <PlusCircleIcon className="w-24 h-24 text-olm-gray m-auto"></PlusCircleIcon>
                        </button>

                        <p className="flex space-x-1 text-olm-black font-medium text-sm">
                            {!customer && error ? (
                                <div className="flex justify-between ">
                                    <p className="flex space-x-1 text-olm-black font-medium text-sm">
                                        <span>
                                            To access your saved uploads,
                                            <Link href="/login">
                                                <a className="underline hover:no-underline ml-1 ">
                                                    log in
                                                </a>
                                            </Link>
                                            <span className="ml-1">or</span>
                                            <Link href="/create-account">
                                                <a className="underline hover:no-underline ml-1 ">
                                                    create an account.
                                                </a>
                                            </Link>
                                        </span>
                                    </p>
                                </div>
                            ) : (
                                <span>
                                    No files uploaded. Click the Upload Files
                                    button to add your first file.
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default UserUploadsComponent
